/**
 * The board listening for its keyboard shortcuts (docs/06-UI-SPEC.md §16).
 *
 * The listener sits on the window rather than on the canvas, because a teacher who
 * has just clicked a button in the panel still expects P to pick up the pen. What a
 * press means is `shortcuts.ts`; what it does is `board-keys.ts`; this only joins the
 * two to the browser and keeps the reference sheet's open state.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { runCommand, type CommandDeps } from './board-keys.js';
import { readShortcut, typingInto } from './shortcuts.js';

/** What the board screen needs back: whether the shortcut sheet is showing. */
export interface Shortcuts {
  readonly helpOpen: boolean;
  readonly closeHelp: () => void;
}

/** The parts of a shortcut only the board screen can carry out. */
export type ShortcutActions = Pick<CommandDeps, 'undo' | 'redo' | 'goToPage'>;

export function useShortcuts(actions: ShortcutActions): Shortcuts {
  const [helpOpen, setHelpOpen] = useState(false);
  // Read inside the listener, so opening the sheet does not detach and reattach it.
  const open = useRef(helpOpen);
  open.current = helpOpen;

  useEffect(() => {
    const deps: CommandDeps = {
      ...actions,
      setHelp: setHelpOpen,
      helpOpen: () => open.current,
    };
    const onKey = (event: KeyboardEvent): void => {
      // A shortcut inside a text box belongs to the words being typed: Ctrl+Z takes
      // back the last word, and P is the letter P.
      if (typingInto(event.target)) return;
      const command = readShortcut(event);
      if (!command) return;
      // Ctrl+A would otherwise select the whole page as text, and Ctrl+- would shrink
      // the browser rather than the board.
      event.preventDefault();
      runCommand(command, deps);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [actions]);

  const closeHelp = useCallback(() => {
    setHelpOpen(false);
  }, []);

  return { helpOpen, closeHelp };
}
